'use client';

/**
 * McpClaudeBeforeAfter — the differentiator section.
 *
 * Two-column "without/with" framing lifted from the existing
 * AIDeveloperExperience component on /features. Concrete contrast:
 * traditional integration takes hours-to-days; with the MCP server it
 * takes minutes.
 *
 * The hero image of Claude Code + SUM sits beside the cards as visual
 * anchor.
 */

import React from 'react';
import { Locale } from '../../../../i18n-config';
import SectionLabel from '../home/SectionLabel';
import { useTranslation } from '@/i18n/client';
import { HiCheckCircle, HiXCircle } from 'react-icons/hi2';

type Props = { lang: Locale };

const McpClaudeBeforeAfter = ({ lang }: Props) => {
  const { t } = useTranslation(lang);

  const label = t('McpClaude/BeforeAfter/badge');
  const headlineA = t('McpClaude/BeforeAfter/title1');
  const headlineB = t('McpClaude/BeforeAfter/title2');
  const sub = t('McpClaude/BeforeAfter/subtitle');

  const traditionalRaw = t('McpClaude/BeforeAfter/traditional', { returnObjects: true });
  const mcpRaw = t('McpClaude/BeforeAfter/mcp', { returnObjects: true });
  const traditional: string[] = Array.isArray(traditionalRaw) ? (traditionalRaw as string[]) : [];
  const mcp: string[] = Array.isArray(mcpRaw) ? (mcpRaw as string[]) : [];

  return (
    <section
      id="before-after"
      className="px-6 py-24 lg:py-28"
      style={{
        background: '#0A0E27',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        color: '#F5F7FA',
      }}
    >
      <div className="mx-auto max-w-[1200px]">
        <SectionLabel num="01" label={label} />
        <h2
          className="font-bold tracking-tight"
          style={{
            fontSize: 'clamp(28px, 3.4vw, 48px)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: '12px 0 14px',
            maxWidth: 820,
          }}
        >
          {headlineA} <span style={{ color: '#B4C1D8' }}>{headlineB}</span>
        </h2>
        <p
          style={{
            color: '#B4C1D8',
            fontSize: 18,
            maxWidth: 720,
            lineHeight: 1.5,
            margin: '0 0 48px',
          }}
        >
          {sub}
        </p>

        <div className="grid gap-5 items-start" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
          <article
            className="rounded-2xl p-6"
            style={{
              background: 'rgba(255,77,77,0.06)',
              border: '1px solid rgba(255,77,77,0.30)',
            }}
          >
            <div
              className="mono flex items-center gap-2"
              style={{
                fontSize: 11,
                letterSpacing: 1.5,
                color: '#FF4D4D',
                textTransform: 'uppercase',
                marginBottom: 12,
              }}
            >
              <HiXCircle style={{ width: 14, height: 14 }} />
              {t('McpClaude/BeforeAfter/traditional_label')}
            </div>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {traditional.map((item, i) => (
                <li
                  key={i}
                  style={{
                    color: '#B4C1D8',
                    fontSize: 14,
                    lineHeight: 1.55,
                    padding: '6px 0',
                    borderTop: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  {item}
                </li>
              ))}
            </ul>
          </article>

          <article
            className="rounded-2xl p-6"
            style={{
              background: 'rgba(34,211,238,0.06)',
              border: '1px solid rgba(34,211,238,0.30)',
            }}
          >
            <div
              className="mono flex items-center gap-2"
              style={{
                fontSize: 11,
                letterSpacing: 1.5,
                color: '#22D3EE',
                textTransform: 'uppercase',
                marginBottom: 12,
              }}
            >
              <HiCheckCircle style={{ width: 14, height: 14 }} />
              {t('McpClaude/BeforeAfter/mcp_label')}
            </div>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {mcp.map((item, i) => (
                <li
                  key={i}
                  style={{
                    color: '#B4C1D8',
                    fontSize: 14,
                    lineHeight: 1.55,
                    padding: '6px 0',
                    borderTop: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  {item}
                </li>
              ))}
            </ul>
          </article>
        </div>
      </div>
    </section>
  );
};

export default McpClaudeBeforeAfter;
